import * as joda from "@js-joda/core";
import { LocalDate, LocalTime } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyleVariadic } from "trader.ts/ui";
import { renderIf } from "trader.ts/ui.common";
import { Form, constantField, dateBox, timeBox } from "trader.ts/ui.forms";
import {
  b2fileid,
  enrollment_status,
  enrollmentid,
  formula_color,
  formula_type,
  internalteacherid,
  lesson_removed_reason,
  lessongroupid,
  lessonid,
  planningmode,
  studentid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import { fileboxWithAutosave } from "../../client/fileinput";
import { renderAsync } from "../../client/renderasync";
import {
  closePopupWhenClickingOutsidePopupOnMonthPage,
  renderMonthsWithSelection,
} from "../../client/rendermonth";
import {
  renderCancelButton,
  renderDeleteButton,
  renderSaveButton,
} from "../../client/savebutton";
import { holiday } from "../../domain/periods";
import { arrayBufferToBase64Client } from "../../lib/arraybuffertobase64";
import {
  formulaColorToPlanningColors,
  makeGradient,
  whitePlanningColors,
} from "../../lib/colors";
import { isEqual } from "../../lib/isequal";
import { range } from "../../lib/range";
import { textboxWithAutosave } from "../../lib/textboxwithautosave";
import {
  lessonStatus,
  parseFormulaColor,
  parseLessonRemovedReason,
} from "../../parsing_from_ur";
import {
  HHmm,
  ddMMMMyyyy,
  renderFirstNameLastName,
  renderLessonRemovedReason,
} from "../../render";
import * as routes from "../../routes";
import { renderClosePopupButton } from "../planning/selectionandforms";
import {
  makeStatusIndicatorStyle,
  renderPresences,
} from "../teacher_lessonroster/client";
import { stringToJsxClientside } from "../editemailtemplates/client";

type mode = null | LocalDate;
const modeC = routes.magicCodec<mode>();
const modeRoute = makeRoute("/selection/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});
const router = makeClientsideRouter(modeRoute, null);

const emptyCell = { className: "", style: "", contents: null };

type opts = {
  userCanPlan: boolean;
  lessongroup: {
    id: lessongroupid;
    time: LocalTime;
    type_: formula_type;
    lessonduration: number;
    lastlesson: null | LocalDate;
    planningmode: planningmode;
    teacher: {
      id: teacherid;
      internalteacherid: internalteacherid;
    };
    formula: {
      color: formula_color;
      numberoflessons: number;
    };
    students:
      | {
          id: studentid;
          firstname: string;
          lastname: string;
          enrollmentid: enrollmentid;
          enrollment_startedlessonson: joda.LocalDate;
          enrollment_stoppedlessonson: joda.LocalDate | null;
          enrollment_status: enrollment_status;
        }[]
      | null;
  };
  monthsinfuture: number | undefined;
  selecteddate: LocalDate | undefined;
  periods: {
    period: { firstday: LocalDate; lastday: LocalDate; holidays: holiday[] };
    nextPeriod: null | {
      firstday: LocalDate;
      lastday: LocalDate;
      holidays: holiday[];
    };
  };
  lessons: {
    id: lessonid;
    date: LocalDate;
    plannedteacherid: teacherid;
    teacherid: teacherid;
    removedreason: lesson_removed_reason | null;
    time: LocalTime;
    replacement: lessonid | null;
    sequence_number: number | null; // null = removed
  }[];
  complementlessons: {
    id: lessonid;
    lessongroupid: lessongroupid;
    date: LocalDate;
    time: LocalTime;
    formula_color: formula_color;
  }[];
  presences: presence[];
};

type presence = {
  lessonid: lessonid;
  studentid: studentid;
  status: lessonStatus;
  createdby: string;
  createdon: LocalDate;
  comments: string;
  storagefileid: b2fileid | null;
  needsadmincheck: boolean;
};

type presenceWithSources = {
  lessonid: lessonid;
  studentid: studentid;
  status: lessonStatus;
  // createdby: string;
  // createdon: LocalDate;
  comments: Source<string>;
  storagefileid: b2fileid | null;
  // needsadmincheck: boolean;
};

export function render({
  lessongroup,
  periods,
  complementlessons,
  lessons,
  monthsinfuture,
  selecteddate,
  presences,
  userCanPlan,
}: opts): HTMLElement | HTMLElement[] {
  const selectedS = router.start();
  if (selectedS.get() === null && selecteddate !== undefined) {
    document.location.hash = "";
    selectedS.set(selecteddate);
  }

  const activeLessons = lessons.filter((l) => l.removedreason === null);

  const flexSection =
    lessongroup.planningmode === "Flex" ? (
      <div style="text-align: right;">
        <section className={style.card + style.card_accent}>
          <h3>{"Beurtenkaart"}</h3>
          <div
            style={
              activeLessons.length > lessongroup.formula.numberoflessons
                ? "color: red;"
                : ""
            }
          >
            {stringToJsxClientside(
              "<b>%count / %total</b> lessen ingepland"
                .replace("%count", activeLessons.length.toString())
                .replace(
                  "%total",
                  lessongroup.formula.numberoflessons.toString(),
                ),
            )}
          </div>
        </section>
      </div>
    ) : (
      <span></span>
    );

  setTimeout(() => {
    const selected = document.querySelector(
      "." + style.yearcalendar_day_selected,
    );
    if (selected) {
      selected.scrollIntoView(false);
    }
  });

  const lessonsWithPresences = lessons.map((l) => ({
    ...l,
    presences: new Source(
      (presences || [])
        .filter((p) => p.lessonid === l.id)
        .map(
          (p: presence): presenceWithSources => ({
            ...p,
            comments: new Source(p.comments),
            status: p.status,
          }),
        ),
    ),
  }));

  const lastday = [
    ...lessons.map((l) => l.date),
    ...complementlessons.map((l) => l.date),
    periods.period.lastday,
  ]
    .maxBy((d) => d.toEpochDay())!
    .plusMonths(monthsinfuture || 0);

  const months = renderMonthsWithSelection({
    period: periods.period,
    nextPeriod: periods.nextPeriod,
    lastday,
    holidays: periods.period.holidays.concat(
      periods.nextPeriod?.holidays || [],
    ),
    today: LocalDate.now(),
    makeLink: (d) => router.makeHashLink(d),
    selectedS,
    saveLinkIntoHistory: false,
    canSelect: () => true,
    renderCell: (
      d: LocalDate,
      _holiday: holiday | null,
    ): {
      className: string;
      style: string;
      contents: null | string | HTMLElement;
    } => {
      const lessons = lessonsWithPresences.filter((lesson) =>
        lesson.date.isEqual(d),
      );
      const activeLessons = lessons.filter((l) => l.removedreason === null);
      if (activeLessons.length === 0 && complementlessons.length > 0) {
        const complementLesson =
          complementlessons.find((lesson) => lesson.date.isEqual(d)) || null;
        if (!complementLesson) {
          return emptyCell;
        } else {
          return {
            className: "",
            style:
              "background: " +
              makeGradient(
                formulaColorToPlanningColors(
                  parseFormulaColor(complementLesson.formula_color),
                ),
              ) +
              ";opacity: 0.4",
            contents: "B",
          };
        }
      } else {
        const color = formulaColorToPlanningColors(
          parseFormulaColor(lessongroup.formula.color),
        );
        const earliestActiveLesson = activeLessons.minBy(
          (l) => l.sequence_number || 0,
        );
        return {
          className: "",
          style: earliestActiveLesson
            ? "background: " + makeGradient(color) + `;color: ${color.text};`
            : "",
          contents: (
            <div>
              {
                // rendering text
                earliestActiveLesson
                  ? "# " +
                    (earliestActiveLesson.sequence_number || 0).toString()
                  : d.dayOfMonth().toString()
              }

              {
                // rendering "deviation" asterisk
                activeLessons.some(
                  (l) =>
                    !isEqual(l.time, lessongroup.time) ||
                    l.teacherid !== l.plannedteacherid,
                ) ? (
                  <span style=" position: absolute; top: -10px; right: 2px; ">
                    *
                  </span>
                ) : (
                  <span></span>
                )
              }

              {
                // rendering square
                lessons.length === 0 ? (
                  <span></span>
                ) : (
                  dynStyleVariadic(
                    activeLessons.flatMap((l) => l.presences),
                    (presences) =>
                      earliestActiveLesson === null
                        ? lessons[0].removedreason &&
                          parseLessonRemovedReason(lessons[0].removedreason) ===
                            "StudentLegalAbsence"
                          ? "background-color: #ae8fff;"
                          : "background-color: #919191;"
                        : makeStatusIndicatorStyle(
                            d,
                            lessongroup.type_,
                            // Multiple lessons -> pass students multiple times
                            range(0, activeLessons.length).flatMap(
                              (_) => lessongroup.students || [],
                            ),
                            presences.flat(),
                          ),
                    <div className={style.lessongroup_status_indicator}></div>,
                  )
                )
              }
            </div>
          ),
        };
      }
    },
    renderPopupContents: (d) =>
      renderPopupContents(
        lessongroup,
        lessonsWithPresences,
        complementlessons,
        d,
        {
          userCanPlan,
        },
      ),
  });

  return (
    <main
      onclick={(ev) =>
        closePopupWhenClickingOutsidePopupOnMonthPage(
          router.makeHashLink(null),
          ev,
          { saveLinkIntoHistory: false },
        )
      }
    >
      {flexSection}

      {months}

      <div>
        <a
          style="font-size: 0.85em;"
          href={routes.lessongroup_lessondataRoute.link({
            lessongroupid: lessongroup.id,
            monthsinfuture: (monthsinfuture || 0) + 3,
            selecteddate: undefined,
          })}
        >
          + {"Bekijk volgend kwartaal"}
        </a>
      </div>
    </main>
  );
}
type lessonWithPresences = opts["lessons"][number] & {
  presences: Source<presenceWithSources[]>;
};
function renderPopupContents(
  lessongroup: opts["lessongroup"],
  lessons: lessonWithPresences[],
  complementlessons: opts["complementlessons"],
  d: LocalDate,
  opts: { userCanPlan: boolean },
): HTMLElement {
  const lessonsFound_ = lessons.filter((l) => l.date.isEqual(d));
  const activeLessonsFound = lessonsFound_.filter(
    (l) => l.removedreason === null,
  );
  const removedLessonsFound = lessonsFound_.filter(
    (l) => l.removedreason !== null,
  );
  const complementLessonFound = complementlessons.find((l) =>
    l.date.isEqual(d),
  );
  const colors =
    activeLessonsFound.length > 0
      ? formulaColorToPlanningColors(
          parseFormulaColor(lessongroup.formula.color),
        )
      : complementLessonFound
      ? formulaColorToPlanningColors(
          parseFormulaColor(complementLessonFound.formula_color),
        )
      : whitePlanningColors;

  const planningLink = complementLessonFound ? (
    <span></span>
  ) : (
    <div style="text-align: right; margin-top: -16px;">
      <a
        style="text-decoration: underline"
        href={
          opts.userCanPlan
            ? routes.planningRoute.link({
                time: activeLessonsFound[0]?.time || lessongroup.time,
                teacher: lessongroup.teacher.internalteacherid, // todo improve?
                date: d,
              })
            : routes.teacher_lessonrosterRoute.link({
                date: d,
                mode: "day",
                teacherid: lessongroup.teacher.id,
              })
        }
      >
        {opts.userCanPlan ? "Planning" : "Lessenrooster"}
        <i className={style.icon_on_right + FA.icon_calendar_alt_regular}></i>
      </a>
    </div>
  );

  const title =
    activeLessonsFound.length === 1
      ? "Les" +
        " " +
        (activeLessonsFound[0].sequence_number || 0).toString()
      : activeLessonsFound.length > 1
      ? "Lessen " +
        " " +
        activeLessonsFound
          .map((l) => l.sequence_number || 0)
          .orderBy((seq) => seq, "asc")
          .join(" en ")
          .toString()
      : complementLessonFound
      ? "Andere A/B lessenreeks"
      : "Geen les";

  function renderPerLesson(
    l: lessonWithPresences,
    hideNewLessonButton: boolean,
  ) {
    const showingPresencesS = new Source(false);
    return (
      <div>
        {!isEqual(l.time, lessongroup.time) ? (
          <article className={style.text_with_icon}>
            <i className={FA.icon_info_solid}></i>
            <div style="font-weight: 600">
              {"Afwijkend tijdstip: %time".replace(
                "%time",
                l.time.format(HHmm),
              )}
            </div>
          </article>
        ) : (
          <span></span>
        )}

        {l.teacherid !== l.plannedteacherid ? (
          <article className={style.text_with_icon}>
            <i className={FA.icon_info_solid}></i>
            <div style="font-weight: 600">
              {renderAsync(
                rpc(routes.getTeacherByIdSpec, undefined, {
                  teacherid: l.teacherid,
                }),
                (t) =>
                  t === null ? (
                    <span></span>
                  ) : (
                    <span>
                      {"Afwijkende docent: %teacher".replace(
                        "%teacher",
                        renderFirstNameLastName(t),
                      )}
                    </span>
                  ),
              )}
            </div>
          </article>
        ) : (
          <span></span>
        )}
        <section>
          {dyn(showingPresencesS, (showingPres) =>
            !showingPres ? (
              <a
                onclick={() => showingPresencesS.set(true)}
                className={style.text_with_icon}
              >
                <h1>{"Aanwezigheden"}</h1>
                <i
                  style="top: -2px;"
                  className={
                    FA.icon_chevron_circle_down_solid + style.icon_on_right
                  }
                ></i>
              </a>
            ) : (
              <h1>{"Aanwezigheden"}</h1>
            ),
          )}
          {renderIf(showingPresencesS, () => (
            <div>
              {renderPresences(
                {
                  lesson: {
                    id: l.id,
                    date: l.date,
                  },
                  lessongroup: { type: lessongroup.type_ },
                  students: lessongroup.students,
                  presences: l.presences,
                },
                (pres) => ({
                  ...pres,
                  storagefileid: null,
                  lessonid: l.id,
                  comments: new Source(""),
                }),
              )}
            </div>
          ))}
        </section>
        {maybeRenderAbsenceSection(
          lessongroup,
          complementlessons,
          l,
          lessongroup.students,
          l.presences,
          {
            userCanPlan: opts.userCanPlan,
            hideNewLessonButton,
          },
        )}
      </div>
    );
  }

  const lastRemovedLesson = removedLessonsFound.maxBy((l) =>
    l.date.toEpochDay(),
  );
  const lastRemovedLessonReplacement =
    (lastRemovedLesson &&
      lessons.find(
        (l) =>
          l.removedreason === null && l.id === lastRemovedLesson.replacement,
      )) ||
    null;

  const content =
    activeLessonsFound.length === 1 ? (
      renderPerLesson(activeLessonsFound[0], false)
    ) : activeLessonsFound.length > 1 ? (
      <div>
        {activeLessonsFound
          .orderBy((l) => l.sequence_number || 0)
          .map((l) => (
            <article className={style.panel}>
              <b>
                {"Les om %time".replace("%time", l.time.format(HHmm))}
              </b>
              {renderPerLesson(l, true)}
            </article>
          ))}
        {renderNewLessonSection(lessongroup, complementlessons, d, {
          label: "Plan nieuwe les",
          userCanPlan: opts.userCanPlan,
          replacingLessonId: null,
          bigButton: false,
        })}
      </div>
    ) : complementLessonFound ? (
      <div>
        <section style="margin-bottom: 16px; display: flex; align-items: flex-start; justify-content: space-between;">
          <a
            href={routes.lessongroup_generalRoute.link({
              lessongroupid: complementLessonFound.lessongroupid,
            })}
          >
            <i className={FA.icon_link_solid}></i>
            {"Details lessenreeks"}
          </a>
          <a
            href={routes.lessongroup_lessondataRoute.link({
              lessongroupid: complementLessonFound.lessongroupid,
              selecteddate: d,
              monthsinfuture: undefined,
            })}
          >
            {"Lesdata"}
            <i
              className={style.icon_on_right + FA.icon_calendar_alt_regular}
            ></i>
          </a>
        </section>
        {renderNewLessonSection(lessongroup, complementlessons, d, {
          label: "Plan nieuwe les",
          userCanPlan: opts.userCanPlan,
          replacingLessonId: null,
          bigButton: false,
        })}
      </div>
    ) : (
      <span>
        {lastRemovedLesson ? (
          <div>
            <article className={style.text_with_icon}>
              <i className={FA.icon_exclamation_circle_solid}></i>
              <div>
                {renderLessonRemovedReason(
                  parseLessonRemovedReason(lastRemovedLesson.removedreason!),
                )}
              </div>
            </article>
            {lastRemovedLesson.replacement !== null &&
            lastRemovedLessonReplacement ? (
              lastRemovedLesson.removedreason &&
              parseLessonRemovedReason(lastRemovedLesson.removedreason) ===
                "MovedDueToTeacherAbsence" ? (
                <article className={style.text_with_icon}>
                  <i className={FA.icon_exclamation_circle_solid}></i>
                  {"Deze les werd verplaatst door de afwezigheid van de docent. Je kan terug zetten door de afwezigheid van de docent te annuleren op de planning."}
                </article>
              ) : (
                <article>
                  {renderDeleteButton({
                    label: "Zet deze les terug en verwijder inhaalles op %date".replace(
                      "%date",
                      ddMMMMyyyy(lastRemovedLessonReplacement.date),
                    ),
                    dontShowLabelAgainInConfirmation: true,
                    confirmationLabel:
                      "Ben je zeker dat je deze les wilt terugzetten?",
                    doDelete: () =>
                      rpc(
                        routes.rollbackStudentAbsenceReplacementLessonSpec,
                        undefined,
                        { replacedLessonId: lastRemovedLesson.id },
                      ),
                    className: style.button_small,
                    containerStyle: "display: block;",
                    style: "width: 100%;",
                    redirectTo: routes.lessongroup_lessondataRoute.link({
                      lessongroupid: lessongroup.id,
                      selecteddate: d,
                      monthsinfuture: undefined,
                    }),
                  })}
                </article>
              )
            ) : (
              <span></span>
            )}
          </div>
        ) : (
          <article>&nbsp;</article>
        )}
        {renderNewLessonSection(lessongroup, complementlessons, d, {
          label: "Plan nieuwe les",
          userCanPlan: opts.userCanPlan,
          replacingLessonId: null,
          bigButton: lessongroup.planningmode === "Flex",
        })}
      </span>
    );

  return (
    <div
      onclick={(ev) => ev.stopPropagation()}
      className={style.panel + style.panel_as_popup}
    >
      <div
        className={style.panel_header}
        style={`background: ${makeGradient(colors)}; color: ${colors.text}`}
      >
        <article>
          <b>{title}</b>
        </article>

        <div>{ddMMMMyyyy(d)}</div>

        {renderClosePopupButton(
          () => (document.location.hash = router.makeHashLink(null)),
        )}
      </div>

      {planningLink}

      {content}
    </div>
  );
}

function renderRemoveLessonSection(
  lessongroup: opts["lessongroup"],
  lesson: opts["lessons"][number],
  opts: { userCanPlan: boolean },
): HTMLElement | HTMLElement[] {
  return !opts.userCanPlan ? (
    <span></span>
  ) : (
    renderDeleteButton({
      label: "Verwijder les",
      confirmationLabel: "Ben je zeker dat je deze les wilt verwijderen?",
      doDelete: () =>
        rpc(routes.deleteLessonSpec, undefined, {
          lessonid: lesson.id,
        }),
      className: style.button_small,
      style: "width: 100%;",
      containerStyle: "display: block;",
      redirectTo: routes.lessongroup_lessondataRoute.link({
        lessongroupid: lessongroup.id,
        monthsinfuture: undefined,
        selecteddate: lesson.date,
      }),
    })
  );
}

function renderNewLessonSection(
  lessongroup: {
    id: lessongroupid;
    time: LocalTime;
    lastlesson: LocalDate | null;
    lessonduration: number;
    teacher: { internalteacherid: internalteacherid };
  },
  _complementlessons: opts["complementlessons"],
  date: LocalDate,
  opts: {
    label: string;
    userCanPlan: boolean;
    replacingLessonId: lessonid | null;
    bigButton: boolean;
  },
): HTMLElement | HTMLElement[] {
  const showingS = new Source(false);
  const form = new Form({})
    .addField("date", [], async () => {
      if (opts.replacingLessonId === null) {
        return constantField(date);
      } else {
        const proposedDates = await rpc(
          routes.getCatchupLessonDatesSpec,
          undefined,
          {
            lessons: [
              {
                id: opts.replacingLessonId,
                date,
                time: lessongroup.time,
                lessonduration: lessongroup.lessonduration,
                internalteacherid: lessongroup.teacher.internalteacherid,
                lessongroupid: lessongroup.id,
              },
            ],
          },
        );
        return dateBox({
          label: "Datum",
          initialVal:
            proposedDates[0]?.proposedDate /* should always be filled */ ||
            lessongroup.lastlesson ||
            LocalDate.now(),
        });
      }
    })
    .addField("time", [], () =>
      timeBox({ initialVal: lessongroup.time, label: "Tijdstip" }),
    )
    .build();
  return !opts.userCanPlan ? (
    <span></span>
  ) : (
    <div>
      {dyn(showingS, (showing) =>
        !showing ? (
          <button
            className={
              (opts.bigButton ? "" : style.button_small) +
              style.text_with_icon +
              style.button_accent
            }
            style="width: 100%; margin-bottom: 8px;"
            onclick={() => showingS.set(true)}
          >
            <i className={FA.icon_plus_solid}></i>
            <div>{opts.label}</div>
          </button>
        ) : (
          <div className={style.panel + style.slide_in}>
            <b>{opts.label}</b>
            {form.render()}
            <div style="text-align: right;">
              {renderCancelButton({ onclick: () => showingS.set(false) })}
              {renderSaveButton({
                formS: form.s,
                save: (form) =>
                  rpc(routes.saveNewLessonSpec, undefined, {
                    lessongroupid: lessongroup.id,
                    date: form.date,
                    time: form.time,
                    replacingLesson: opts.replacingLessonId,
                  }),
                timeout: 1500,
                reload: true,
              })}
            </div>
          </div>
        ),
      )}
    </div>
  );
}

function renderLessonNoAbsenceContent(
  lessongroup: opts["lessongroup"],
  complementlessons: opts["complementlessons"],
  lesson: lessonWithPresences,
  opts: { userCanPlan: boolean; hideNewLessonButton: boolean },
) {
  // Extra div's to avoid margins
  return (
    <div>
      <article>{renderRemoveLessonSection(lessongroup, lesson, opts)}</article>
      {opts.hideNewLessonButton ? (
        <span></span>
      ) : (
        <div>
          {renderNewLessonSection(lessongroup, complementlessons, lesson.date, {
            label: "Plan nieuwe les",
            userCanPlan: opts.userCanPlan,
            replacingLessonId: null,
            bigButton: false,
          })}
        </div>
      )}
    </div>
  );
}

function renderLessonAbsenceContent(
  lessongroup: opts["lessongroup"],
  complementlessons: opts["complementlessons"],
  lesson: { id: lessonid; date: LocalDate },
  stu: { id: studentid },
  pres: presenceWithSources,
  opts: { userCanPlan: boolean },
) {
  const commentsInput = textboxWithAutosave({
    source: pres.comments,
    label: "Extra info bij afwezigheid",
    type: "textarea",
    save: (s) =>
      rpc(routes.saveStudentAbsenceCommentsSpec, undefined, {
        s,
        lessonid: lesson.id,
        studentid: stu.id,
      }),
  });
  const file = pres.storagefileid ? (
    <a
      href={routes.downloadStudentAbsenceFileSpec.route.link({
        lessonid: lesson.id,
        studentid: stu.id,
      })}
      target="_blank"
      className={style.a_around_button}
    >
      <button className={style.button_em_down}>
        <div className={style.text_with_icon}>
          <i className={FA.icon_link_solid}></i>
          {"Document opgeslagen"}
        </div>
      </button>
    </a>
  ) : (
    fileboxWithAutosave({
      initiallabel: "Voeg document toe",
      selectedlabel: "",
      className: style.button_em_down,
      doSave: (file) =>
        rpc(routes.uploadStudentAbsenceFileSpec, undefined, {
          lessonid: lesson.id,
          studentid: stu.id,
          file: {
            mimetype: file.mimetype,
            fileb64: arrayBufferToBase64Client(file.data),
          },
        }).finally(() => document.location.reload()),
    })
  );
  return (
    <div>
      <section>
        {commentsInput}

        <div style="display: flex">
          <div style="flex: 1"></div>
          {file}
        </div>
      </section>

      {renderNewLessonSection(lessongroup, complementlessons, lesson.date, {
        label: "Plan vervangles",
        userCanPlan: opts.userCanPlan,
        replacingLessonId: lesson.id,
        bigButton: false,
      })}
    </div>
  );
}

function maybeRenderAbsenceSection(
  lessongroup: opts["lessongroup"],
  complementlessons: opts["complementlessons"],
  lesson: lessonWithPresences,
  students: { id: studentid }[] | null,
  presencesS: Source<presenceWithSources[]>,
  opts: { userCanPlan: boolean; hideNewLessonButton: boolean },
): HTMLElement | HTMLElement[] {
  if ((students || []).length > 1) {
    return renderLessonNoAbsenceContent(
      lessongroup,
      complementlessons,
      lesson,
      opts,
    );
  }
  const stu = (students || [])[0];
  return dyn(presencesS, (presences) => {
    const pres = presences && presences.find((pre) => pre.studentid === stu.id);
    if (!pres) {
      return renderLessonNoAbsenceContent(
        lessongroup,
        complementlessons,
        lesson,
        opts,
      );
    }
    if (pres.status !== "StudentAbsent") {
      return renderLessonNoAbsenceContent(
        lessongroup,
        complementlessons,
        lesson,
        opts,
      );
    }
    return renderLessonAbsenceContent(
      lessongroup,
      complementlessons,
      lesson,
      stu,
      pres,
      opts,
    );
  });
}
